import React, { Component } from 'react'
import PropTypes from 'prop-types'

import dictionaries from './dictionaries'
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getLanguage,
  normalizeLanguage,
} from './languages'
import { createDomTranslationObserver } from './domTranslator'
import { readStoredLanguage, storeLanguage } from './storage'

export const translate = (language, key) => {
  const normalizedLanguage = normalizeLanguage(language)
  const dictionary = dictionaries[normalizedLanguage] || {}
  const english = dictionaries[DEFAULT_LANGUAGE] || {}

  return dictionary[key] || english[key] || key
}

export const TranslationContext = React.createContext({
  currentLanguage: DEFAULT_LANGUAGE,
  languages: SUPPORTED_LANGUAGES,
  selectedLanguage: getLanguage(DEFAULT_LANGUAGE),
  setLanguage: () => {},
  t: (key) => translate(DEFAULT_LANGUAGE, key),
})

class TranslationProvider extends Component {
  constructor(props) {
    super(props)

    this.setLanguage = this.setLanguage.bind(this)
    this.valueCache = null
    this.domTranslationObserver = null

    this.state = {
      currentLanguage: readStoredLanguage(),
    }
  }

  componentDidMount() {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return
    }

    this.domTranslationObserver = createDomTranslationObserver(
      () => this.state.currentLanguage
    )
    this.domTranslationObserver.start()
  }

  componentDidUpdate() {
    if (this.domTranslationObserver) {
      this.domTranslationObserver.apply()
    }
  }

  componentWillUnmount() {
    if (this.domTranslationObserver) {
      this.domTranslationObserver.stop()
      this.domTranslationObserver = null
    }
  }

  setLanguage(language) {
    const currentLanguage = normalizeLanguage(language)

    if (currentLanguage === this.state.currentLanguage) {
      return
    }

    storeLanguage(currentLanguage)

    this.setState({ currentLanguage })
  }

  getValue(currentLanguage) {
    if (
      this.valueCache &&
      this.valueCache.currentLanguage === currentLanguage
    ) {
      return this.valueCache
    }

    this.valueCache = {
      currentLanguage,
      languages: SUPPORTED_LANGUAGES,
      selectedLanguage: getLanguage(currentLanguage),
      setLanguage: this.setLanguage,
      t: (key) => translate(currentLanguage, key),
    }

    return this.valueCache
  }

  render() {
    const value = this.getValue(this.state.currentLanguage)

    return (
      <TranslationContext.Provider value={value}>
        {this.props.children}
      </TranslationContext.Provider>
    )
  }
}

TranslationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const withTranslation = (WrappedComponent) => {
  const WithTranslation = (props) => (
    <TranslationContext.Consumer>
      {(translation) => <WrappedComponent {...props} {...translation} />}
    </TranslationContext.Consumer>
  )

  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  WithTranslation.displayName = `withTranslation(${displayName})`

  return WithTranslation
}

export default TranslationProvider
